import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, ArrowDownRight, ArrowUpRight } from "lucide-react";

export default function PriceAlertsBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: notifs = [] } = useQuery({
    queryKey: ["price-notifs"],
    queryFn: async () => (await api.get("/pricing/notifications")).data,
    refetchInterval: 60000,
  });
  const unread = notifs.filter((n) => !n.read);

  const markAll = useMutation({
    mutationFn: async () => api.post("/pricing/notifications/read", { all: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-notifs"] });
      qc.invalidateQueries({ queryKey: ["price-notif-count"] });
    },
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative" data-testid="price-alerts-bell">
          <Bell className="h-4 w-4" />
          {unread.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold" data-testid="price-alerts-count">
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-semibold text-sm">Price Alerts</p>
          {unread.length > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => markAll.mutate()} data-testid="price-alerts-mark-read">
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No price alerts yet. Run a market scan from the Price Matrix.</p>
          ) : (
            notifs.slice(0, 20).map((n) => (
              <button
                key={n.id}
                onClick={() => navigate("/dealership/pricing")}
                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-accent transition-colors ${!n.read ? "bg-amber-500/[0.07]/60" : ""}`}
                data-testid={`price-notif-${n.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {n.action === "lower" ? <ArrowDownRight className="h-3.5 w-3.5 text-red-400 shrink-0" /> : <ArrowUpRight className="h-3.5 w-3.5 text-green-400 shrink-0" />}
                  <span className="text-sm font-semibold truncate">{n.vehicle_desc}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {n.action === "lower" ? "Lower" : "Raise"} to {money(n.suggested_price)} (now {money(n.current_price)})
                </p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
